import { useEffect, useId, useRef, useState, type PointerEvent } from "react";
import { Button } from "@/components/ui/button";
import type { HandoverEvidence } from "@/lib/oprema";

export function SignaturePad({
  onChange,
  disabled = false,
  label = "Potpis radnika",
}: {
  onChange: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pointer = useRef<number | null>(null);
  const revision = useRef(0);
  const callback = useRef(onChange);
  useEffect(() => {
    callback.current = onChange;
  }, [onChange]);
  const [error, setError] = useState("");
  const signed = useRef(false);
  const helpId = useId();
  useEffect(
    () => () => {
      revision.current++;
    },
    [],
  );
  function position(event: PointerEvent<HTMLCanvasElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x:
        (event.clientX - bounds.left) *
        (event.currentTarget.width /
          (bounds.width || event.currentTarget.width)),
      y:
        (event.clientY - bounds.top) *
        (event.currentTarget.height /
          (bounds.height || event.currentTarget.height)),
    };
  }
  function start(event: PointerEvent<HTMLCanvasElement>) {
    if (
      disabled ||
      pointer.current !== null ||
      (event.pointerType === "mouse" && event.button !== 0)
    )
      return;
    const context = event.currentTarget.getContext("2d");
    if (!context) {
      setError("Potpis nije dostupan u ovom pregledniku.");
      return;
    }
    revision.current++;
    onChange(null);
    signed.current = false;
    setError("");
    pointer.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { x, y } = position(event);
    context.lineWidth = 3;
    context.lineCap = "round";
    context.strokeStyle = "#172b3a";
    context.beginPath();
    context.moveTo(x, y);
    event.preventDefault();
  }
  function move(event: PointerEvent<HTMLCanvasElement>) {
    if (disabled || pointer.current !== event.pointerId) return;
    const context = event.currentTarget.getContext("2d");
    const { x, y } = position(event);
    context?.lineTo(x, y);
    context?.stroke();
    signed.current = true;
    event.preventDefault();
  }
  function finish(event: PointerEvent<HTMLCanvasElement>) {
    if (pointer.current !== event.pointerId) return;
    pointer.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    const version = revision.current;
    if (!signed.current) return;
    try {
      event.currentTarget.toBlob((blob) => {
        if (version !== revision.current) return;
        if (blob)
          callback.current(
            new File([blob], "potpis.png", { type: "image/png" }),
          );
        else setError("Potpis nije moguće spremiti. Ponovite potpis.");
      }, "image/png");
    } catch {
      setError("Potpis nije moguće spremiti. Ponovite potpis.");
    }
  }
  return (
    <div className="space-y-2">
      <p id={helpId}>Potpišite se prstom ili mišem iznad crte.</p>
      <canvas
        ref={canvas}
        width={640}
        height={200}
        aria-label={label}
        aria-describedby={helpId}
        className="h-40 w-full rounded-lg border bg-white"
        style={{
          touchAction: "none",
          backgroundImage:
            "linear-gradient(to bottom, transparent 79%, #94a3b8 79%, #94a3b8 80%, transparent 80%)",
        }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={finish}
        onPointerCancel={finish}
        onPointerLeave={finish}
        onLostPointerCapture={finish}
      />
      {error && <p role="alert">{error}</p>}
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={disabled}
        onClick={() => {
          revision.current++;
          pointer.current = null;
          canvas.current?.getContext("2d")?.clearRect(0, 0, 640, 200);
          signed.current = false;
          setError("");
          onChange(null);
        }}
      >
        Obriši potpis
      </Button>
    </div>
  );
}
function LocalPhoto({ file, index }: { file: File; index: number }) {
  const element = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    if (element.current) element.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <img
      ref={element}
      alt={`Fotka ${index + 1}`}
      className="h-24 w-28 rounded object-cover"
    />
  );
}
export function PhotoPicker({
  photos,
  onChange,
  disabled = false,
  optional = false,
}: {
  photos: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  optional?: boolean;
}) {
  const inputId = useId();
  const [error, setError] = useState("");

  return (
    <div className="space-y-3">
      <label
        htmlFor={inputId}
        className={`flex min-h-14 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-4 font-semibold ${disabled ? "opacity-50" : "bg-slate-50"}`}
      >
        {optional ? "Slikaj (preporučeno)" : "Slikaj radnika s opremom"}
      </label>
      <input
        id={inputId}
        className="sr-only"
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        multiple
        disabled={disabled}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (
            files.some(
              (file) => !["image/jpeg", "image/png"].includes(file.type),
            )
          ) {
            setError("Odaberite JPEG ili PNG fotografije.");
          } else {
            setError("");
            onChange([...photos, ...files]);
          }
          event.target.value = "";
        }}
      />
      {error && <p role="alert">{error}</p>}
      <ul className="flex flex-wrap gap-3">
        {photos.map((file, index) => (
          <li key={`${index}:${file.name}`} className="w-28">
            <LocalPhoto file={file} index={index} />
            <Button
              className="min-h-11 w-full"
              type="button"
              variant="outline"
              disabled={disabled}
              aria-label={`Ukloni fotku ${index + 1}`}
              onClick={() =>
                onChange(photos.filter((_, position) => position !== index))
              }
            >
              Ukloni
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
export function PrimopredajaStep({
  value,
  onChange,
  canSkipPhoto = false,
  disabled = false,
  meta,
  errors = {},
}: {
  value: HandoverEvidence;
  onChange: (value: HandoverEvidence) => void;
  canSkipPhoto?: boolean;
  disabled?: boolean;
  meta?: string;
  errors?: Record<string, string>;
}) {
  const [skip, setSkip] = useState(Boolean(value.noPhotoReason));
  return (
    <section
      className="space-y-4 rounded-lg border bg-white p-4"
      aria-label="Fotka i potpis"
    >
      <h2 className="text-lg font-semibold">Fotka i potpis</h2>
      {meta && <p className="text-sm">{meta}</p>}
      <PhotoPicker
        photos={value.photos}
        onChange={(photos) => {
          setSkip(false);
          onChange({ ...value, photos, noPhotoReason: "" });
        }}
        disabled={disabled}
      />
      {errors["fotke[]"] && <p role="alert">{errors["fotke[]"]}</p>}
      {canSkipPhoto && !value.photos.length && (
        <>
          <label className="flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              disabled={disabled}
              checked={skip}
              onChange={(event) => {
                setSkip(event.target.checked);
                if (!event.target.checked)
                  onChange({ ...value, noPhotoReason: "" });
              }}
            />
            Izdaj bez fotke
          </label>
          {skip && (
            <label className="block">
              Razlog izdavanja bez fotke (obavezno)
              <textarea
                className="block min-h-24 w-full rounded border p-3"
                required
                maxLength={2000}
                disabled={disabled}
                value={value.noPhotoReason}
                onChange={(event) =>
                  onChange({ ...value, noPhotoReason: event.target.value })
                }
              />
              {errors.bez_fotke_razlog && (
                <span role="alert">{errors.bez_fotke_razlog}</span>
              )}
            </label>
          )}
        </>
      )}
      <SignaturePad
        onChange={(signature) => onChange({ ...value, signature })}
        disabled={disabled}
      />
      {errors.potpis && <p role="alert">{errors.potpis}</p>}
    </section>
  );
}
